import moment from 'moment'
import axios from 'axios'

import apiSettings from '@/api/settings'
import { EventBus } from '@/event-bus.js'
import Auth0AuthService from '@/auth/Auth0AuthService'
import SessionAuthService from '@/auth/SessionAuthService'

var auth = {}
if (process.env.VUE_APP_AUTH_MODE === 'session') {
  // Autenticacion por Cookies de Session
  auth = new SessionAuthService()
} else {
  // Por defecto se autentica con AUTH0
  auth = new Auth0AuthService()
}

// initial state
const state = {
  isAuthenticated: !!localStorage.getItem('access_token'),
  accessToken: localStorage.getItem('access_token'),
  idToken: localStorage.getItem('id_token'),
  msToRenewToken: localStorage.getItem('ms_to_renew_token'),
  tokenExpiryWarning: localStorage.getItem('token_expiry_warning'),
  expiresAt: localStorage.getItem('expires_at'),
  avatar: localStorage.getItem('avatar') ? localStorage.getItem('avatar') : 'https://via.placeholder.com/125x125',
  role: localStorage.getItem('role') ? localStorage.getItem('role') : '',
  roles: localStorage.getItem('roles') !== null && localStorage.getItem('roles') !== 'undefined' ? JSON.parse(localStorage.getItem('roles')) : [],
  username: localStorage.getItem('username') ? localStorage.getItem('username') : '',
  tokenRenewalTimeout: false,
  routesConfigs: localStorage.getItem('routesConfigs') === null ? [] : JSON.parse(localStorage.getItem('routesConfigs')),
  id: null,
  extra: {},
  // Calculo el cancelToken a utilizar apenas carga la app
  // https://github.com/lumile/lumbre/issues/611
  cancelTokenSource: axios.CancelToken.source()
}

const getters = {
  isAuthenticated (state) {
    return state.isAuthenticated
  }
}

const actions = {
  login ({ commit }, data) {
    return auth.login(data)
  },
  logout ({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (state.isAuthenticated) {
        // Cancelo todos los requests pendientes de ejecución en axios
        // https://github.com/lumile/lumbre/issues/611
        state.cancelTokenSource.cancel()
        // Solo intenta llamar al endpoint de deslogueo si el usuario esta logueado
        // esto es para evitar el loop infinito que puede darse al intentar
        // desloguarse no estando logueado, cosa que retorna un 401 y el loop
        // vuelve a empezar x el control de 401 impuesto en AxiosSetup.js
        auth.logout()
      }
      commit('settings/mutateResetDashSettings', false, { root: true })
      commit('mutateLogout')
      EventBus.$emit('routePush', { name: 'login' })
      resolve()
    })
  },
  handleAuthentication ({ commit, dispatch, state }) {
    return auth.handleAuthentication()
      .then(authResult => {
        commit('mutateAuthenticated', authResult)
        // Se dispara la acción para completar la configuracion del VueRouter
        // dispatch('getRoutesConfigs')
        const tokenRenewalTimeout = setTimeout(() => {
          dispatch('updateAuthTokens')
        }, state.msToRenewToken)
        commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
      })
      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.log(error)
        }
        dispatch('logout')
      })
  },
  getUserInfo ({ commit }) {
    return auth.getUserInfo()
      .then(user => {
        commit('mutateProfile', user)
        return user
      })
      .catch(error => {
        if (process.env.NODE_ENV === 'development') {
          console.log(error)
        }
      })
  },
  updateAuthTokens ({ commit, dispatch, state }) {
    if (process.env.NODE_ENV === 'development') {
      console.log('El token expiro y se tiene que renovar')
    }

    return new Promise((resolve, reject) => {
      auth.updateTokens()
        .then(authResult => {
          commit('mutateAuthenticated', authResult)
          // Callback
          const tokenRenewalTimeout = setTimeout(() => {
            dispatch('updateAuthTokens')
          }, state.msToRenewToken)
          commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
          resolve()
        })
        .catch(error => {
          if (process.env.NODE_ENV === 'development') {
            console.log('store/session error:', error)
          }
          reject(error)
          dispatch('logout')
        })
    })
  },
  reScheduleUpdateAuthTokens ({ commit, dispatch, state }) {
    if (state.isAuthenticated) {
      if (process.env.NODE_ENV === 'development') {
        console.log('reScheduleUpdateAuthTokens')
      }
      const tokenRenewalTimeout = setTimeout(() => {
        dispatch('updateAuthTokens')
      }, state.msToRenewToken)
      commit('mutateTokenRenewalTimeout', tokenRenewalTimeout)
    }
  },
  /**
   * Dependencia del tk #164 - funcionalidad freezada reimplementada en router.js
   * se deja logica por unos commits para confirmar si se va a utilizar vuex para
   * persistir los datos del router o no.
   * @param {*} param0
   */
  getRoutesConfigs ({ commit }) {
    return apiSettings.getRoutesConfigs()
      .then(response => {
        commit('mutateRoutesConfig', response)
      })
      .catch(error => {
        console.log(error)
      })
  }
}

const mutations = {
  mutateAuthenticated (state, authData) {
    state.isAuthenticated = true
    state.accessToken = authData.accessToken
    state.idToken = authData.idToken
    // The expires_in parameter indicates how many seconds the Access Token will be valid for, and can be used to anticipate expiration of the Access Token.
    // https://auth0.com/docs/api-auth/tutorials/silent-authentication#access-token-expiration
    // expiresAt is the representation in epoch time of when the token will expiry.
    state.expiresAt = authData.expiresIn * 1000 + new Date().getTime()

    // tokenExpiryWarning es un epoch time que representa la fecha en la cual se va a gestionar la renovacion del
    // token, esta fecha esta basada en state.expiresAt menos process.env.VUE_APP_JWT_TOKEN_RENEW_MINUTES_BEFORE_EXPIRY
    state.tokenExpiryWarning = moment(state.expiresAt).subtract(process.env.VUE_APP_JWT_TOKEN_RENEW_MINUTES_BEFORE_EXPIRY, 'minutes')

    state.msToRenewToken = state.tokenExpiryWarning.diff(moment())
    if (process.env.NODE_ENV === 'development') {
      console.log('expiresIn', authData.expiresIn)
      console.log('expiresAt', state.expiresAt)
      console.log('msToRenewToken', state.msToRenewToken)
    }
    localStorage.setItem('access_token', state.accessToken)
    localStorage.setItem('id_token', state.idToken)
    localStorage.setItem('expires_at', state.expiresAt)
    localStorage.setItem('ms_to_renew_token', state.msToRenewToken)
    localStorage.getItem('token_expiry_warning', state.tokenExpiryWarning)
  },
  mutateLogout (state) {
    // Dejo seteado un nuevo valor para el canceltoken luego del logout.
    // https://github.com/lumile/lumbre/issues/611
    state.cancelTokenSource = axios.CancelToken.source()
    clearTimeout(state.tokenRenewalTimeout)
    state.isAuthenticated = false
    state.accessToken = null
    state.idToken = false
    state.routesConfigs = []
    state.extra = {}
    state.id = null
    localStorage.removeItem('access_token')
    localStorage.removeItem('id_token')
    localStorage.removeItem('expires_at')
    localStorage.removeItem('ms_to_renew_token')
    localStorage.removeItem('token_expiry_warning')
    localStorage.removeItem('avatar')
    localStorage.removeItem('role')
    localStorage.removeItem('roles')
    localStorage.removeItem('username')
    localStorage.removeItem('routesConfigs')
  },
  mutateProfile (state, userProfile) {
    if (userProfile.avatar) {
      state.avatar = userProfile.avatar
    } else {
      state.avatar = userProfile.picture
    }
    state.username = userProfile.nickname
    if (userProfile.role) {
      state.role = userProfile.role
    } else {
      state.role = ''
    }
    if (userProfile.extra) {
      state.extra = userProfile.extra
    } else {
      state.extra = {}
    }

    if (userProfile.id) {
      state.id = userProfile.id
    } else {
      state.id = null
    }
    if (process.env.VUE_APP_ROLLBAR_ENABLE === 'yes' && process.env.NODE_ENV !== 'development' && this._vm?.$rollbar) {
      this._vm.$rollbar.configure({
        payload: {
          person: {
            id: state.id
          }
        }
      })
    }
    state.roles = userProfile.roles

    localStorage.setItem('avatar', state.avatar)
    localStorage.setItem('username', state.username)
    localStorage.setItem('role', state.role)
    localStorage.setItem('roles', JSON.stringify(state.roles))
  },
  mutateTokenRenewalTimeout (state, newTokenRenewalTimeout) {
    const oldTokenRenewalTimeout = state.tokenRenewalTimeout
    clearTimeout(oldTokenRenewalTimeout)
    if (process.env.NODE_ENV === 'development') {
      console.log('mutateTokenRenewalTimeout', newTokenRenewalTimeout)
      console.log('msToRenewToken', state.msToRenewToken)
    }
    state.tokenRenewalTimeout = newTokenRenewalTimeout
  },
  /**
   * Dependencia del tk #164 - funcionalidad freezada reimplementada en router.js
   * se deja logica por unos commits para confirmar si se va a utilizar vuex para
   * persistir los datos del router o no.
   * @param {} state
   * @param {*} apiResponse
   */
  mutateRoutesConfig (state, response) {
    state.routesConfigs = response.routesConfigs
    localStorage.setItem('routesConfigs', JSON.stringify(state.routesConfigs))
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
